// The manager's CRM decisions as document changes. Each action returns the changes to write and the changes that
// undo them (the documents as they were), so every action can be undone from the snackbar.

import type {
  CrmChild,
  CrmCustomer,
  CrmEnquiry,
  CustomerDoc,
  MergeSuggestion,
  RejectedPair,
  Stage,
} from "../../shared/crm";
import { pairId } from "../../shared/crm";
import type { DocChange } from "../../shared/crm";

export interface Change {
  customers: DocChange[];
  pairs: { id: string; pair: RejectedPair | null }[];
}

export interface Planned {
  apply: Change;
  revert: Change;
}

const now = () => new Date().toISOString();
const noteId = () => Math.random().toString(36).slice(2, 10);

/** Builds the undo for a set of document writes from the documents as they are now. */
const plan = (docs: Map<string, CustomerDoc>, customers: DocChange[], pairs: Change["pairs"] = [], previousPairs: Change["pairs"] = []): Planned => ({
  apply: { customers, pairs },
  revert: {
    customers: customers.map((c) => ({ id: c.id, doc: docs.get(c.id) ?? null })),
    pairs: previousPairs,
  },
});

/**
 * The family as one stored document: the earliest document of the family, with the members, notes and child facts of
 * any other documents that ended up in it. Other documents are deleted, except stored enquiries, which keep their
 * enquiry (it is an identity of the family) and lose the rest.
 */
const consolidate = (customers: CrmCustomer[], docs: Map<string, CustomerDoc>, newId: () => string) => {
  const all = customers.flatMap((c) => [c.doc, ...c.extraDocIds.map((id) => docs.get(id) ?? null)]).filter((d): d is CustomerDoc => !!d)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
  const primary = all[0];
  const stamp = now();
  const merged: CustomerDoc = {
    id: primary?.id ?? (customers.length === 1 ? customers[0]!.id : newId()),
    members: Array.from(new Set(customers.flatMap((c) => c.identities.map((i) => i.key)))).sort(),
    createdAt: primary?.createdAt ?? stamp,
    updatedAt: stamp,
    displayName: all.find((d) => d.displayName)?.displayName ?? null,
    stageOverride: all.find((d) => d.stageOverride)?.stageOverride ?? null,
    notes: all.flatMap((d) => d.notes ?? []),
    childInfo: Object.assign({}, ...all.slice().reverse().map((d) => d.childInfo ?? {})),
    childGroups: Object.assign({}, ...all.slice().reverse().map((d) => d.childGroups ?? {})),
    enquiry: primary?.enquiry ?? null,
  };
  const others: DocChange[] = all.slice(1).map((d) => ({
    id: d.id,
    doc: d.enquiry ? { id: d.id, members: [], createdAt: d.createdAt, updatedAt: stamp, enquiry: d.enquiry } : null,
  }));
  return { merged, others };
};

export const createActions = (docs: Map<string, CustomerDoc>, newId: () => string) => {
  /** Saves one family after `edit` changes its consolidated document. */
  const update = (customer: CrmCustomer, edit: (doc: CustomerDoc) => CustomerDoc): Planned => {
    const { merged, others } = consolidate([customer], docs, newId);
    return plan(docs, [{ id: merged.id, doc: edit(merged) }, ...others]);
  };

  return {
    /** Stored members always link, so an earlier "not the same" between the two no longer matters. */
    merge: (a: CrmCustomer, b: CrmCustomer): Planned => {
      const { merged, others } = consolidate([a, b], docs, newId);
      return plan(docs, [{ id: merged.id, doc: merged }, ...others]);
    },

    reject: (suggestion: MergeSuggestion): Planned => {
      const [a, b] = suggestion.pair;
      const id = pairId(a, b);
      return plan(docs, [], [{ id, pair: { id, a, b, at: now() } }], [{ id, pair: null }]);
    },

    /** Moves one identity out into a family of its own and remembers that the two are different. */
    split: (customer: CrmCustomer, identityKey: string): Planned => {
      const { merged, others } = consolidate([customer], docs, newId);
      const remaining = merged.members.filter((k) => k !== identityKey);
      const stamp = now();
      const changes: DocChange[] = [{ id: merged.id, doc: { ...merged, members: remaining } }, ...others];
      const enquiryId = identityKey.startsWith("man:") ? identityKey.slice(4) : null;
      if (enquiryId && enquiryId === merged.id) {
        // The family's own document is the enquiry: give the rest of the family a new document instead.
        const id = newId();
        changes[0] = { id: merged.id, doc: { id: merged.id, members: [], createdAt: merged.createdAt, updatedAt: stamp, enquiry: merged.enquiry } };
        changes.push({ id, doc: { ...merged, id, members: remaining, enquiry: null } });
      } else if (!enquiryId) {
        const id = newId();
        changes.push({ id, doc: { id, members: [identityKey], createdAt: stamp, updatedAt: stamp } });
      }
      const other = remaining[0];
      const pairs: Change["pairs"] = [];
      const previousPairs: Change["pairs"] = [];
      if (other) {
        const id = pairId(identityKey, other);
        pairs.push({ id, pair: { id, a: identityKey, b: other, at: stamp } });
        previousPairs.push({ id, pair: null });
      }
      return plan(docs, changes, pairs, previousPairs);
    },

    addNote: (customer: CrmCustomer, text: string) =>
      update(customer, (d) => ({ ...d, notes: [...(d.notes ?? []), { id: noteId(), at: now(), text }] })),

    deleteNote: (customer: CrmCustomer, id: string) =>
      update(customer, (d) => ({ ...d, notes: (d.notes ?? []).filter((n) => n.id !== id) })),

    setStage: (customer: CrmCustomer, stage: Stage | null, note: string) =>
      update(customer, (d) => ({ ...d, stageOverride: stage ? { stage, note, at: now() } : null })),

    rename: (customer: CrmCustomer, name: string) => update(customer, (d) => ({ ...d, displayName: name.trim() || null })),

    setChildDob: (customer: CrmCustomer, child: CrmChild, dateOfBirth: string | null) =>
      update(customer, (d) => {
        const childInfo = { ...(d.childInfo ?? {}) };
        child.identityKeys.forEach((k) => delete childInfo[k]);
        if (dateOfBirth) childInfo[child.key] = { dateOfBirth };
        // the child key can change when children are regrouped, so store it on every identity of the child
        if (dateOfBirth) child.identityKeys.forEach((k) => { childInfo[k] = { dateOfBirth }; });
        return { ...d, childInfo };
      }),

    /** Moves one appearance of a child to another child of the family, or to a child of its own (target null). */
    moveToChild: (customer: CrmCustomer, identityKey: string, target: CrmChild | null) =>
      update(customer, (d) => {
        const childGroups = { ...(d.childGroups ?? {}) };
        // Label every appearance so the manual grouping sticks for the whole family.
        for (const child of customer.children) child.identityKeys.forEach((k) => { childGroups[k] = child.key; });
        childGroups[identityKey] = target ? target.key : `child-${noteId()}`;
        return { ...d, childGroups };
      }),

    createEnquiry: (enquiry: CrmEnquiry): Planned => {
      const id = newId();
      const stamp = now();
      return plan(docs, [{ id, doc: { id, members: [], createdAt: stamp, updatedAt: stamp, enquiry } }]);
    },
  };
};

export type CrmActions = ReturnType<typeof createActions>;
